import { ChatConversation, ChatMessage } from '../interfaces/Chat';
import apiService from './api.service';

class ChatService {
  // Cache per le conversazioni
  private conversationsCache: { data: ChatConversation[] | null; timestamp: number } = {
    data: null,
    timestamp: 0
  };
  private messagesCache: Map<number, { data: ChatMessage[]; timestamp: number }> = new Map();
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minuti

  // Ottieni tutte le conversazioni
  async getConversations(forceRefresh = false): Promise<{ conversations: ChatConversation[] }> {
    if (!forceRefresh && this.conversationsCache.data &&
      Date.now() - this.conversationsCache.timestamp < this.CACHE_DURATION) {
      return { conversations: this.conversationsCache.data };
    }

    const response = await apiService.get('/ai/');

    // Verifica che la risposta contenga i dati attesi
    // Ora response.data è direttamente l'array delle conversazioni
    if (!response.data || !Array.isArray(response.data)) {
      console.error('❌ API response structure is invalid:', response.data);
      throw new Error('Invalid API response structure - expected array of conversations');
    }

    // Salva in cache
    this.conversationsCache = {
      data: response.data, // Ora response.data è direttamente l'array
      timestamp: Date.now()
    };

    // Restituisci nel formato atteso dal componente
    return { conversations: response.data };
  }

  // Ottieni una conversazione specifica
  async getConversation(chatId: number, forceRefresh = false): Promise<{ messages: ChatMessage[] }> {
    if (!forceRefresh) {
      const cached = this.messagesCache.get(chatId);
      if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return { messages: cached.data };
      }
    }

    const response = await apiService.get('/ai/conversation/', {
      params: { chat_id: chatId }
    });

    // Salva in cache
    this.messagesCache.set(chatId, {
      data: response.data.messages,
      timestamp: Date.now()
    });

    return response.data;
  }

  // Invia un messaggio all'AI
  async sendMessage(content: string, chatId?: number | null, userId?: string): Promise<{
    content: string;
    conversation: number;
  }> {
    const response = await apiService.post('/ai/ask_llm/', {
      content,
      chat_id: chatId,
      user_id: userId || sessionStorage.getItem('userId')
    });

    // Invalida la cache delle conversazioni e dei messaggi
    this.conversationsCache = { data: null, timestamp: 0 };
    if (chatId) {
      this.messagesCache.delete(chatId);
    }

    return response.data;
  }

  // Crea una nuova conversazione
  async createConversation(title: string): Promise<ChatConversation> {
    const response = await apiService.post('/ai/conversations/', { title });

    // Invalida la cache delle conversazioni
    this.conversationsCache = { data: null, timestamp: 0 };

    return response.data;
  }

  // Elimina una conversazione
  async deleteConversation(chatId: number): Promise<void> {
    await apiService.delete(`/ai/conversations/${chatId}/`);

    // Invalida la cache
    this.conversationsCache = { data: null, timestamp: 0 };
    this.messagesCache.delete(chatId);
  }

  // Rinomina una conversazione
  async renameConversation(chatId: number, newTitle: string): Promise<void> {
    await apiService.patch(`/ai/conversations/${chatId}/`, { title: newTitle });

    // Invalida la cache delle conversazioni
    this.conversationsCache = { data: null, timestamp: 0 };
  }

  // Modifica una conversazione
  async editChat(chatId: number, data: { title?: string;[key: string]: any }): Promise<void> {
    // Mappa title in name per il backend
    const backendData = { ...data };
    if (data.title) {
      backendData.name = data.title;
      delete backendData.title;
    }

    await apiService.patch(`/ai/${chatId}/`, backendData);

    // Invalida la cache delle conversazioni e dei messaggi
    this.conversationsCache = { data: null, timestamp: 0 };
    this.messagesCache.delete(chatId);
  }

  // Elimina una conversazione
  async deleteChat(chatId: number): Promise<void> {
    await apiService.delete(`/ai/${chatId}/`);

    // Invalida la cache delle conversazioni e dei messaggi
    this.conversationsCache = { data: null, timestamp: 0 };
    this.messagesCache.delete(chatId);
  }

  // Ottieni il conteggio dei token utilizzati
  async getTokenUsage(): Promise<{ tokens_used: number; tokens_limit: number }> {
    const response = await apiService.get('/ai/token-usage/');
    return response.data;
  }

  // Helper per mappare i messaggi dal backend
  mapBackendMessage(msg: any): ChatMessage {
    return {
      id: msg.id,
      sender: msg.role === 'user' ? 'user' : 'ai',
      content: msg.content,
      timestamp: msg.created_at
    };
  }

  // Invalida la cache
  clearCache(): void {
    this.conversationsCache = { data: null, timestamp: 0 };
    this.messagesCache.clear();
  }

  invalidateConversationCache(chatId?: number): void {
    if (chatId) {
      this.messagesCache.delete(chatId);
    } else {
      this.conversationsCache = { data: null, timestamp: 0 };
    }
  }
}

// Esporta istanza singleton
const chatService = new ChatService();
export default chatService;
